import axios from 'axios';
import { accessTokenStore } from './accessTokenStore';
import { refreshSession, extractAccessToken, extractRotatedAccessToken } from './authApi';
import { isRememberMeEnabled } from './cookies';
import { accessTokenPersistOptions } from './tokenPersist';
import { emitApiSuccess, emitSessionExpired } from './sessionEvents';

let getUserId = () => null;
let getRememberMeFlag = () => false;
let interceptorsInstalled = false;
let refreshPromise = null;

export function bindAuthUserIdGetter(getter) {
  getUserId = typeof getter === 'function' ? getter : () => null;
}

export function bindRememberMeGetter(getter) {
  getRememberMeFlag = typeof getter === 'function' ? getter : () => false;
}

function isAuthPath(url = '') {
  return /auth\/(login|refresh|logout)/i.test(String(url));
}

export async function runRefresh() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const uid = getUserId();
      const res = await refreshSession({ uid });
      const token = extractAccessToken(res);
      if (!token) {
        throw new Error('Refresh succeeded but no accessToken returned');
      }
      accessTokenStore.set(token, accessTokenPersistOptions(getRememberMeFlag()));
      return token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Missing accessToken:
 * - rememberMe=true → silent refresh
 * - rememberMe=false → session expired → login
 */
export async function ensureAccessToken() {
  const existing = accessTokenStore.get();
  if (existing) return existing;

  if (isRememberMeEnabled(getRememberMeFlag())) {
    try {
      return await runRefresh();
    } catch (err) {
      emitSessionExpired();
      throw err;
    }
  }

  emitSessionExpired();
  const err = new Error('Session expired');
  err.code = 'SESSION_EXPIRED';
  throw err;
}

export function installAxiosAuthInterceptors() {
  // Callers already include REACT_APP_EZTS_API_BASE_URL in the request path
  // (`/ezts/expense/all`). Setting baseURL as well produced `/ezts/ezts/...`.
  axios.defaults.baseURL = undefined;
  axios.defaults.withCredentials = false;

  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.interceptors.request.use(async (config) => {
    if (isAuthPath(config.url)) return config;

    try {
      const token = await ensureAccessToken();
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Let the request go; response / caller handles auth failure
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      if (!isAuthPath(response?.config?.url)) {
        const rotated = extractRotatedAccessToken(response);
        if (rotated) {
          accessTokenStore.set(rotated, accessTokenPersistOptions(getRememberMeFlag()));
        }
        emitApiSuccess();
      }
      return response;
    },
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;

      if (status !== 401 || !original || isAuthPath(original.url) || original._retry) {
        return Promise.reject(error);
      }

      // rememberMe=true: silent refresh + retry once. Otherwise expire → login.
      if (!isRememberMeEnabled(getRememberMeFlag())) {
        emitSessionExpired();
        return Promise.reject(error);
      }

      original._retry = true;
      try {
        const token = await runRefresh();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return axios(original);
      } catch (refreshErr) {
        emitSessionExpired();
        return Promise.reject(refreshErr);
      }
    }
  );
}
